import React from 'react';
import type { UserInterview } from '@axiom/validation';
import {
  Badge,
  DataGridContextMenuItem,
  Button,
  IconButton,
  Layout,
} from '@axiom/ui';
import type { CellRenderProps, GetContextMenuItemsParams } from '@axiom/ui';
import { Link } from 'react-router';
import {
  InterviewsStatus,
  InterviewBadgeColors,
  Opportunity,
} from '@axiom/const';

import { InterviewGridAttendeesModal } from '../components/InterviewGridAttendeesModal/InterviewGridAttendeesModal';
import { InterviewGridDateAndRound } from '../components/InterviewGridDateAndRound/InterviewGridDateAndRound';
import { InterviewGridAttendeesModalUtil } from '../components/InterviewGridAttendeesModal/interview-grid-attendees-modal-util';

type FunctionProps = { data: UserInterview };

const noEventStatusSet = new Set([
  InterviewsStatus.REQUESTED,
  InterviewsStatus.CANCELLED,
  InterviewsStatus.UNKNOWN,
]);

const displayDefault = '--';

const CONTEXT_MENU_OPTIONS = {
  editAttendees: {
    name: 'Edit Attendees',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'EDIT_ATTENDEES_MENU_ITEM',
    },
    action: () => {},
  },
  editMeetingLink: {
    name: 'Edit Meeting Link',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'EDIT_MEETING_LINK_MENU_ITEM',
    },
    action: () => {},
  },
  editInterviewTime: {
    name: 'Edit Interview Time',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'EDIT_INTERVIEW_TIME_MENU_ITEM',
    },
    action: () => {},
  },
  dismissRequest: {
    name: 'Dismiss requested interview',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'DISMISS_REQUEST_MENU_ITEM',
    },
    action: () => {},
  },
  schedule: {
    name: 'Schedule New Interview',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'SCHEDULE_NEW_MENU_ITEM',
    },
    action: () => {},
  },
  scheduleNow: {
    name: 'Schedule Now',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'SCHEDULE_NOW_MENU_ITEM',
    },
    action: () => {},
  },
  cancel: {
    name: 'Cancel Interview',
    menuItem: DataGridContextMenuItem,
    menuItemParams: {
      testId: 'CANCEL_MENU_ITEM',
    },
    action: () => {},
  },
};

type ContextMenuOptionKey = keyof typeof CONTEXT_MENU_OPTIONS;

const isClosedOpportunity = (data: UserInterview) =>
  data.stage === Opportunity.CLOSED_WON ||
  data.stage === Opportunity.CLOSED_LOST;

const getContextMenuOptionKeys = (
  data: UserInterview
): ContextMenuOptionKey[] => {
  let keys: ContextMenuOptionKey[];

  switch (data.interviewStatus) {
    case InterviewsStatus.LIVE:
    case InterviewsStatus.TODAY:
    case InterviewsStatus.CONFIRMED:
    case InterviewsStatus.SCHEDULED: {
      keys = [
        'cancel',
        'editInterviewTime',
        'editAttendees',
        'editMeetingLink',
        'schedule',
      ];

      break;
    }
    case InterviewsStatus.PAST:
    case InterviewsStatus.CANCELLED: {
      keys = ['schedule'];

      break;
    }
    case InterviewsStatus.UNKNOWN: {
      keys = ['scheduleNow'];

      break;
    }
    case InterviewsStatus.REQUESTED: {
      keys = ['scheduleNow', 'dismissRequest'];

      break;
    }
    default: {
      keys = [];
    }
  }

  return isClosedOpportunity(data)
    ? keys.filter(key => key === 'cancel')
    : keys;
};

export const InterviewGridCellsUtil = {
  status: ({ data }: FunctionProps) => {
    const background = InterviewBadgeColors[data.interviewStatus];
    return (
      <Badge name="STATUS_BADGE" background={background}>
        {data.interviewStatus}
      </Badge>
    );
  },
  talent: ({ data }: FunctionProps) => {
    return data.candidateOpportunity.candidate.calculatedDisplayName;
  },
  opportunity: ({ data }: FunctionProps) => {
    return (
      <Link
        data-test="OPPORTUNITY_LINK"
        to={`/opportunity-detail/${data.opportunityId}`}
        target="_blank"
      >
        {data.jobName ?? ''}
      </Link>
    );
  },
  dateAndRound: (
    { data }: FunctionProps,
    { onClick }: { onClick: (props: UserInterview) => void }
  ) => {
    if (
      [InterviewsStatus.REQUESTED, InterviewsStatus.UNKNOWN].includes(
        data.interviewStatus
      )
    ) {
      return (
        <Button
          name="SCHEDULE_NOW_BUTTON"
          variation="minimal"
          pattern="secondary"
          onClick={() => {
            onClick(data);
          }}
        >
          SCHEDULE NOW
        </Button>
      );
    }

    return <InterviewGridDateAndRound data={data} />;
  },
  sortAttendees: InterviewGridAttendeesModalUtil.sortAttendees,
  attendees: ({ data }: FunctionProps) => {
    const { invitees, participants } = data.calendarConversation ?? {
      invitees: [],
      participants: [],
    };

    if (noEventStatusSet.has(data.interviewStatus) || invitees.length === 0) {
      return displayDefault;
    }

    return (
      <InterviewGridAttendeesModal
        invitees={invitees}
        participants={participants}
        status={data.interviewStatus}
      />
    );
  },
  meetingLink: (
    { data }: FunctionProps,
    { onAddLink }: { onAddLink: (arg: UserInterview) => void }
  ) => {
    const { meetingLink, meetingLinkType } = data.calendarConversation ?? {
      meetingLink: null,
      meetingLinkType: null,
    };

    if (noEventStatusSet.has(data.interviewStatus)) {
      return displayDefault;
    }

    const linkTypeBadge = meetingLinkType ? (
      <Badge
        name="MEETING_LINK_TYPE_BADGE"
        background={meetingLinkType === 'ZOOM' ? 'blue' : 'purple'}
      >
        {meetingLinkType}
      </Badge>
    ) : null;

    return meetingLink ? (
      <Layout position="middle left">
        <IconButton
          name="MEETING_LINK_BUTTON"
          onClick={() => {
            window.open(meetingLink, '_blank');
          }}
          icon="open"
          pattern="secondary"
          variation="minimal"
        />
        {linkTypeBadge}
      </Layout>
    ) : (
      <div className="text-align-left">
        <Button
          name="ADD_LINK_BUTTON"
          onClick={() => {
            onAddLink(data);
          }}
          pattern="secondary"
          variation="minimal"
        >
          Add Link
        </Button>
      </div>
    );
  },
  getContextMenuItems: ({
    params,
    openScheduleInterviewModal,
    openEditAttendeesModal,
    openEditInterviewTimeModal,
    openInterviewGridCancelModal,
    onDismissRequest,
  }: {
    params: GetContextMenuItemsParams<UserInterview>;
    openScheduleInterviewModal: (arg: UserInterview) => void;
    openEditAttendeesModal: (arg: UserInterview) => void;
    openEditInterviewTimeModal: (arg: UserInterview) => void;
    openInterviewGridCancelModal: (arg: UserInterview) => void;
    onDismissRequest: (arg: UserInterview) => void;
  }): Array<{
    name: string;
    menuItem: typeof DataGridContextMenuItem;
    menuItemParams: { testId: string };
    action: () => void;
  }> => {
    const { column, node } = params;

    if (!node?.data || column?.getColId() !== 'contextMenu') {
      return [];
    }

    const { data } = node;

    CONTEXT_MENU_OPTIONS.editAttendees.action = () => {
      openEditAttendeesModal(data);
    };
    CONTEXT_MENU_OPTIONS.editMeetingLink.action = () => {
      openEditAttendeesModal(data);
    };
    CONTEXT_MENU_OPTIONS.editInterviewTime.action = () => {
      openEditInterviewTimeModal(data);
    };
    CONTEXT_MENU_OPTIONS.schedule.action = () => {
      openScheduleInterviewModal(data);
    };
    CONTEXT_MENU_OPTIONS.scheduleNow.action = () => {
      openScheduleInterviewModal(data);
    };
    CONTEXT_MENU_OPTIONS.cancel.action = () => {
      openInterviewGridCancelModal(data);
    };
    CONTEXT_MENU_OPTIONS.dismissRequest.action = () => {
      onDismissRequest(data);
    };

    return getContextMenuOptionKeys(data).map(key => CONTEXT_MENU_OPTIONS[key]);
  },
  contextMenu: (props: CellRenderProps<UserInterview>) => {
    const { node, column, value, api } = props;

    if (node.data && getContextMenuOptionKeys(node.data).length === 0) {
      return null;
    }

    return (
      <IconButton
        name={`CONTEXT_MENU_${node.data?.id ?? 'unknown'}`}
        icon="vertical-ellipsis"
        variation="minimal"
        pattern="secondary"
        onClick={event => {
          api.showContextMenu({
            rowNode: node,
            column,
            value,
            x: event.clientX,
            y: event.clientY,
          });
        }}
      />
    );
  },
};
